import React, { ErrorInfo } from 'react';

import AppErrorFallback from './AppErrorFallback';

export type GlobalErrorFallbackProps = {
  error: Error;
  errorInfo: ErrorInfo | null;
  resetErrorBoundary: () => void;
};

type GlobalErrorBoundaryProps = React.PropsWithChildren<{
  fallback?: React.ComponentType<GlobalErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}>;

type GlobalErrorBoundaryState = {
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

const INITIAL_STATE: GlobalErrorBoundaryState = {
  error: null,
  errorInfo: null,
};

class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  public constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = INITIAL_STATE;
  }

  public static getDerivedStateFromError(
    error: Error,
  ): Partial<GlobalErrorBoundaryState> {
    return {
      error,
      errorInfo: null,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError } = this.props;

    this.setState({ errorInfo });
    onError?.(error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState(INITIAL_STATE);
  };

  public override render(): React.ReactNode {
    const { children, fallback: FallbackComponent = AppErrorFallback } =
      this.props;
    const { error, errorInfo } = this.state;

    if (error) {
      return (
        <FallbackComponent
          error={error}
          errorInfo={errorInfo}
          resetErrorBoundary={this.handleReset}
        />
      );
    }

    return children;
  }
}

export default GlobalErrorBoundary;
